import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useSavedAddressStore } from './savedAddressStore'
import { usePriceStore } from './servicePriceStore'
import { fetchAvailableSlotsWithEmployees } from '../services/availableSlots'

export const useCustomerDetailsStore = defineStore('customerDetails', () => {
  const priceStore = usePriceStore()
  const isEditMode = ref(false)
  const testDefaults = ref(false)

  const getIsEditMode = computed(() => isEditMode.value)

  const savedAddressStore = useSavedAddressStore();
  
  const serviceDate = ref(new Date())
  const serviceTime = ref('')

  const canProceed = computed(() => {
    return serviceTime.value !== ''
  })

  // Function to fill company invoice fields from saved address
  const fillCompanyInvoiceFields = () => {
    if (savedAddressStore.savedAddress?.btwId) {
      btwId.value = savedAddressStore.savedAddress.btwId
    }
    if (savedAddressStore.savedAddress?.kvk) {
      kvk.value = savedAddressStore.savedAddress.kvk
    }
  }

  const customerCity = ref('')
  const customerStreet = ref('')
  const customerHouseNumber = ref('')
  const customerApartmentNumber = ref('')
  const customerPostCode = ref('')
  const customergateCode = ref('')
  const customerapartmentCode = ref('')
  const customerStaircaseCode = ref('')
  const isCompany = ref(false)

  const clientPresence = ref(true)

  const contactName = ref('')
  const contactPhoneNumber = ref('')
  const contactEmail = ref('')
  const residentialAddress = ref('')
  const btwId = ref('')
  const kvk = ref('')
  const wantCompanyInvoice = ref(false)
  const additionalInfo = ref('')

  // Worker selection fields
  const selectedWorkerId = ref('')

  // Netherlands test data
  const populateTestDefaults = () => {
    if (!testDefaults.value) return
    
    customerCity.value = 'Amsterdam'
    customerStreet.value = 'Prinsengracht'
    customerHouseNumber.value = '263'
    customerApartmentNumber.value = '2A'
    customerPostCode.value = '1016 GV'
    customergateCode.value = '1234'
    customerapartmentCode.value = 'A'
    customerStaircaseCode.value = '2'
    isCompany.value = true
    clientPresence.value = true
    contactName.value = 'Jan van der Berg'
    contactPhoneNumber.value = '+31 6 12345678'
    contactEmail.value = 'jan.vandenberg@example.nl'
    additionalInfo.value = 'Graag bellen voor toegang tot het gebouw'
  }

  // Initialize test defaults on store creation
  if (testDefaults.value) {
    populateTestDefaults()
  }

  const backendSlots = ref([])
  const isFetching = ref(false)
  const getIsFetching = computed(() => isFetching.value)
  const lastFetchedDate = ref(null)

  const fetchSlots = async (date) => {
    // Create a new date at the start of the day in local timezone
    const normalizedDate = new Date(date);
    normalizedDate.setHours(12, 0, 0, 0); // Set to noon to avoid any timezone edge cases

    // Format the date consistently, accounting for timezone
    const newDateStr = new Date(normalizedDate.getTime() - (normalizedDate.getTimezoneOffset() * 60000))
      .toISOString()
      .split('T')[0];

    console.log('Attempting to fetch slots:', {
      newDate: newDateStr,
      lastFetched: lastFetchedDate.value,
      isFetching: isFetching.value
    });

    if (isFetching.value) {
      console.log('Already fetching, skipping');
      return;
    }

    if (lastFetchedDate.value === newDateStr) {
      console.log('Same date, skipping fetch');
      return;
    }

    isFetching.value = true;
    try {
      const slots = await fetchAvailableSlotsWithEmployees(normalizedDate);
      backendSlots.value = slots;
      lastFetchedDate.value = newDateStr;
    } catch (error) {
      console.error('Error fetching slots:', error);
      backendSlots.value = [];
    } finally {
      isFetching.value = false;
    }
  };


  // Watch for date changes to trigger fetch
  watch(serviceDate, (newDate) => {
    console.log(newDate)
    fetchSlots(newDate)
  }, { immediate: true })

  const availableTimeSlots = computed(() => {
    const priceStore = usePriceStore();
    const totalTimeEstimate = priceStore.totalTimeEstimate || 0;
    const timeBlockDuration = 0.5; // Half-hour blocks
    const minimumHoursAhead = priceStore.onlineServiceForm ? 1 : 24;; // Minimum hours required ahead of current time
    console.log("minimum hours ahead: " + minimumHoursAhead)
    const bufferTime = 30; // 30-minute buffer after occupied slots

    const slots = [];
    const totalTimeEstimateInHours = totalTimeEstimate / 60; 
    console.log('estimate: ' + totalTimeEstimateInHours)
    const requiredBlocks = Math.ceil(totalTimeEstimateInHours / timeBlockDuration);

    // Get current time
    const now = new Date();
    const currentDay = new Date(serviceDate.value);
    const isToday = currentDay.toDateString() === now.toDateString();
    
    // Calculate minimum booking time (3 hours from now)
    const minBookingTime = new Date(now.getTime() + (minimumHoursAhead * 60 * 60 * 1000));

    // Convert backend slots to Map for faster lookup with employee data
    const availableSlotsMap = new Map();
    backendSlots.value.forEach(slot => {
      availableSlotsMap.set(slot.timeSlot, slot.availableEmployees || []);
    });

    // Function to format time from minutes to HH:mm
    const formatTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60) % 24;
      const minutes = totalMinutes % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    // Create a Set to track occupied slots and their buffer periods
    const unavailableSlots = new Set();
    
    // First pass: identify all occupied slots and add buffer periods
    for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
      const timeSlot = formatTime(minutes);
      
      // If this slot is not available (occupied), mark it and the next 30 minutes as unavailable
      if (!availableSlotsMap.has(timeSlot)) {
        unavailableSlots.add(timeSlot);
        
        // Add 30-minute buffer after occupied slot
        const bufferSlot = formatTime(minutes + bufferTime);
        if (minutes + bufferTime < 24 * 60) { // Ensure we don't go beyond the day
          unavailableSlots.add(bufferSlot);
        }
      }
    }

    // Function to check if a time block is after minimum booking time
    const isWithinDay = (checkTime) => {
      const [hours, minutes] = checkTime.split(':').map(Number);
      
      // Create a Date object for the selected date and time
      const selectedDateTime = new Date(currentDay);
      selectedDateTime.setHours(hours, minutes, 0, 0);
      
      // Check if the selected date/time is at least minimumHoursAhead from now
      return selectedDateTime >= minBookingTime;
    };

    // Second pass: iterate through 30-minute blocks and check availability
    for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
      const startTime = formatTime(minutes);
      
      // Check if there are enough consecutive available slots
      let isAvailableBlock = true;
      for (let i = 0; i < requiredBlocks; i++) {
        const checkTime = formatTime(minutes + i * 30);
        if (!isWithinDay(checkTime) || 
            !availableSlotsMap.has(checkTime) || 
            unavailableSlots.has(checkTime)) {
          isAvailableBlock = false;
          break;
        }
      }

      slots.push({
        time: startTime,
        available: isAvailableBlock,
        availableEmployees: availableSlotsMap.get(startTime) || [],
      });
    }

    return slots;
  });


watch(availableTimeSlots, (newSlots) => {
  // Check if current serviceTime exists and is available in the new slots
  const isCurrentTimeValid = newSlots.some(
    slot => slot.time === serviceTime.value && slot.available
  );

  // If current time is not valid anymore, clear it
  if (!isCurrentTimeValid && serviceTime.value !== '') {
    serviceTime.value = '';
  }
}, { immediate: true });





watch(isEditMode, (newValue) => console.log("IsEditMode: " + newValue))

  const setEditingMode = (value) => {
    isEditMode.value = value
  }



  const updateFromFetchedOrder = (order) => {
    // console.log('parsed order:', order)

    priceStore.updateFromFetchedOrder(order)

    // Update basic service details
    
    // Handle one-time order
    serviceDate.value = order.cleaningDate 
      ? (order.cleaningDate.toDate 
        ? order.cleaningDate.toDate() 
        : new Date(order.cleaningDate))
      : new Date()
    serviceTime.value = order.cleaningTime || '12:30'


    // Update address details
    customerCity.value = order.customerCity || ''
    customerStreet.value = order.customerStreet || ''
    customerHouseNumber.value = order.customerHouseNumber || ''
    customerApartmentNumber.value = order.customerApartmentNumber || ''
    customerPostCode.value = order.postCode || ''
    customergateCode.value = order.gateCode || ''
    customerapartmentCode.value = order.apartmentCode || ''
    customerStaircaseCode.value = order.staircaseCode || ''

    // Update company status
    isCompany.value = order.customerType == 'firma' || false

    // Update client presence
    clientPresence.value = order.clientPresence !== false

    // Update contact details
    contactName.value = order.contactName || ''
    contactPhoneNumber.value = order.contactPhoneNumber || ''
    contactEmail.value = order.contactEmail || ''
    residentialAddress.value = order.residentialAddress || ''
    btwId.value = order.btwId || ''
    kvk.value = order.kvk || ''
    wantCompanyInvoice.value = order.wantCompanyInvoice || false
    additionalInfo.value = order.additionalInfo || ''
  }

  const clearValues = () => {
    serviceDate.value = new Date()
    serviceTime.value = '12:30'
    
    customerCity.value = ''
    customerStreet.value = ''
    customerHouseNumber.value = ''
    customerApartmentNumber.value = ''
    customerPostCode.value = ''
    customergateCode.value = ''
    customerapartmentCode.value = ''
    customerStaircaseCode.value = ''
    
    isCompany.value = false
    
    clientPresence.value = true
    
    contactName.value = ''
    contactPhoneNumber.value = ''
    contactEmail.value = ''
    residentialAddress.value = ''
    btwId.value = ''
    kvk.value = ''
    wantCompanyInvoice.value = false
    additionalInfo.value = ''
    selectedWorkerId.value = ''
  }


  const setCustomerDetail = (detail, value) => {
    switch (detail) {
      case 'serviceDate':
        serviceDate.value = value
        break
      case 'serviceTime':
        serviceTime.value = value
        break
      case 'city':
        customerCity.value = value
        break
      case 'street':
        customerStreet.value = value
        break
      case 'houseNumber':
        customerHouseNumber.value = value
        break
      case 'apartmentNumber':
        customerApartmentNumber.value = value
        break
      case 'postCode':
        customerPostCode.value = value
        break
      case 'gateCode':
        customergateCode.value = value
        break
      case 'apartmentCode':
        customerapartmentCode.value = value
        break
      case 'staircaseCode':
        customerStaircaseCode.value = value
        break
      case 'isCompany':
        isCompany.value = value
        break
      case 'clientPresence':
        clientPresence.value = (value == 'Zostawię klucz' ? false : true)
        break
      case 'contactName':
        contactName.value = value
        break
      case 'contactPhoneNumber':
        contactPhoneNumber.value = value
        break
      case 'contactEmail':
        contactEmail.value = value
        break
      case 'residentialAddress':
        residentialAddress.value = value
        break
      case 'btwId':
        btwId.value = value
        break
      case 'kvk':
        kvk.value = value
        break
      case 'wantCompanyInvoice':
        wantCompanyInvoice.value = value
        break
      case 'additionalInfo':
        additionalInfo.value = value
        break
      case 'selectedWorkerId':
        selectedWorkerId.value = value
        break
      case 'testDefaults':
        testDefaults.value = value
        break
    }
  }

  const getCustomerDetails = () => {
    return {
      serviceDate: serviceDate.value,
      serviceTime: serviceTime.value,
      customerCity: customerCity.value,
      customerStreet: customerStreet.value,
      customerHouseNumber: customerHouseNumber.value,
      customerApartmentNumber: customerApartmentNumber.value,
      postCode: customerPostCode.value,
      gateCode: customergateCode.value,
      apartmentCode: customerapartmentCode.value,
      staircaseCode: customerStaircaseCode.value,
      isCompany: isCompany.value,
      additionalInfo: additionalInfo.value,
      clientPresence: clientPresence.value,
      contactName: contactName.value,
      contactPhoneNumber: contactPhoneNumber.value,
      contactEmail: contactEmail.value,
      residentialAddress: residentialAddress.value,
      btwId: btwId.value,
      kvk: kvk.value,
      wantCompanyInvoice: wantCompanyInvoice.value,
      selectedWorkerId: selectedWorkerId.value,
    }
  }

  const getContactDetails = computed(() => {
    return {
      contactName: contactName.value,
      contactPhoneNumber: contactPhoneNumber.value,
      contactEmail: contactEmail.value,
      residentialAddress: residentialAddress.value,
    }
  })

  const getCompanyDetails = computed(() => {
    return {
      btwId: btwId.value,
      kvk: kvk.value,
      wantCompanyInvoice: wantCompanyInvoice.value
    }
  })

  // Manual function to load saved contact details from savedAddressStore
  const loadSavedContactDetails = () => {
    if (savedAddressStore.savedAddress) {
      const savedData = savedAddressStore.savedAddress
      
      // Only update the 7 saved fields if they exist in savedAddressStore
      if (savedData.contactName !== undefined) {
        contactName.value = savedData.contactName || ''
      }
      if (savedData.contactPhoneNumber !== undefined) {
        contactPhoneNumber.value = savedData.contactPhoneNumber || ''
      }
      if (savedData.contactEmail !== undefined) {
        contactEmail.value = savedData.contactEmail || ''
      }
      if (savedData.residentialAddress !== undefined) {
        residentialAddress.value = savedData.residentialAddress || ''
      }
      if (savedData.btwId !== undefined) {
        btwId.value = savedData.btwId || ''
      }
      if (savedData.kvk !== undefined) {
        kvk.value = savedData.kvk || ''
      }
      if (savedData.wantCompanyInvoice !== undefined) {
        wantCompanyInvoice.value = savedData.wantCompanyInvoice || false
      }
    }
  }

  // Flag to ensure initial load happens only once
  let hasInitiallyLoaded = false

  // Watcher to automatically load saved contact details on initialization
  watch(
    () => [savedAddressStore.isAuthReady, savedAddressStore.savedAddress],
    ([isAuthReady, savedAddress]) => {
      if (isAuthReady && savedAddress && !hasInitiallyLoaded) {
        loadSavedContactDetails()
        hasInitiallyLoaded = true
      }
    },
    { immediate: true }
  )

  const getAddressDetails = computed(() => {
    return {
      customerCity: customerCity.value,
      customerStreet: customerStreet.value,
      customerHouseNumber: customerHouseNumber.value,
      customerApartmentNumber: customerApartmentNumber.value,
      postCode: customerPostCode.value,
      gateCode: customergateCode.value,
      apartmentCode: customerapartmentCode.value,
      staircaseCode: customerStaircaseCode.value,
      isCompany: isCompany.value,
      additionalInfo: additionalInfo.value,
      clientPresence: clientPresence.value,
    }
  })

  return {
    customerCity,
    isCompany,
    contactName,
    contactPhoneNumber,
    contactEmail,
    residentialAddress,
    btwId,
    kvk,
    wantCompanyInvoice,

    serviceDate,
    setCustomerDetail,
    getCustomerDetails,
    getCompanyDetails,
    getAddressDetails,
    getContactDetails,
    setEditingMode,

    updateFromFetchedOrder,
    clearValues,
    getIsEditMode,
    availableTimeSlots,
    isFetching,
    getIsFetching,
    canProceed,
    selectedWorkerId,
    testDefaults,
    fillCompanyInvoiceFields,
    loadSavedContactDetails,
  }
})
